import { useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { recoveringLoad, selectContext } from '../redux/features/context/contextSlice';
import { setSnackbar } from '../redux/features/snackbarSlice';
import { hasFilters, stripFilters } from './useFacetFilters';

// Where to go when URL is refused by the server.
const HOME_PATH = '/explore';

// 4xx says the request itself is bad, so there is something to fix.
const isBadRequest = (code) => Number(code) >= 400 && Number(code) < 500;

/**
 * What a failed view load does. On a cold load: the filter it named is dropped,
 * or, with no filter to blame, go home. Everything else is reported and left alone.
 */
export default function useLoadErrorHandler(entity) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const context = useSelector(selectContext);

  const latest = useRef();
  latest.current = {
    context, searchParams, setSearchParams, navigate, pathname,
  };

  return useCallback((error) => {
    const { current } = latest;
    let note = '';

    if (!current.context && isBadRequest(error?.code)) {
      if (hasFilters(current.searchParams)) {
        dispatch(recoveringLoad());
        current.setSearchParams((prev) => stripFilters(prev), { replace: true });
        note = ' - filter cleared';
      } else if (current.pathname !== HOME_PATH && current.pathname !== '/') {
        dispatch(recoveringLoad());
        current.navigate(HOME_PATH, { replace: true });
      }
    }

    const message = error?.message || `Failed to load ${entity}.`;
    dispatch(setSnackbar({
      snackbarOpen: true,
      snackbarType: 'error',
      snackbarMessage: `${message}${note}`,
    }));
  }, [dispatch, entity]);
}
